/**
 * PageGuide - the plain-language "what is this page / what do I do here" note
 * (comprehension layer, 2026-06-23).
 *
 * It was a full-width box above the content, shown until dismissed once per
 * page. It is a small "In plain terms" button now (10 Oct 2026): a first-time
 * user opens it, and nobody has to dismiss anything to see the page.
 *
 * `always` is for the one sentence a reader must not miss, a caveat on how to
 * read the numbers. It stays on the page beside the button.
 */
import { type ReactNode } from 'react';
import { HelpTip } from '@foctta/ui-shell';
import { cn } from '@/lib/cn';

export function PageGuide({
  id,
  children,
  always,
  className,
}: {
  /** Stable per-page key; kept so each page's guide can be found in the document. */
  id: string;
  children: ReactNode;
  always?: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <div
      data-page-guide={id}
      className={cn('mb-3 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-muted-foreground', className)}
    >
      <HelpTip label="In plain terms" caption="In plain terms">
        {children}
      </HelpTip>
      {always && (
        <span data-page-guide-always className="text-foreground/80">
          <span aria-hidden="true" className="mx-1 text-muted-foreground/50">|</span>
          {always}
        </span>
      )}
    </div>
  );
}
